import { TablePagination } from '@material-ui/core'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { Typography } from '@mui/material'
import Chip from '@mui/material/Chip'
import { Sidescroll, Stack } from 'components'
import intl from 'localization/components'
import MaterialTable from 'material-table'
import { useState } from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'
import { countries as Countries } from 'utils/constants'
import AddOrEditZone from './components/AddOrEditZone'
import DeleteZone from './components/DeleteZone'
import { icons, localization } from './GlobalDataTable.model'
import { globalDataTableTheme } from './globalDataTableTheme'

const GlobalDataTable = ({ zones, showTitle = true }) => {
  const queryClient = useQueryClient()
  const reactIntl = useIntl()
  const messages = reactIntl.messages
  const [isAddOrEditZone, setIsAddOrEditZone] = useState(false)
  const [isDeleteZone, setIsDeleteZone] = useState(false)
  const [editableData, setEditableData] = useState(undefined)
  const [deleteData, setDeleteData] = useState(undefined)
  const { id: selectedCustomer } = useParams()

  const columns = (messages) => {
    return [
      {
        title: messages[intl.generic('zone-name')],
        field: 'name',
        customFilterAndSearch: (term, rowData) => {
          const formattedCountries = rowData?.countries
            ?.map((country) => country?.toLowerCase())
            ?.join(' ')

          return (
            rowData?.name?.toLowerCase()?.includes(term?.toLowerCase()) ||
            formattedCountries.includes(term?.toLowerCase())
          )
        },
        render: ({ name }) => {
          return (
            <Typography sx={{ textTransform: 'capitalize' }}>{name}</Typography>
          )
        },
      },
      {
        title: messages[intl.generic('zone-countires')],
        field: 'countries',
        customFilterAndSearch: (term, rowData) => {
          const formattedCountries = rowData?.countries
            ?.map((country) => country?.toLowerCase())
            ?.join(' ')

          return (
            rowData?.name?.toLowerCase()?.includes(term?.toLowerCase()) ||
            formattedCountries.includes(term?.toLowerCase())
          )
        },
        render: (rowData) => {
          return (
            <Stack
              direction="row"
              spacing={1}
              wrap="wrap"
              row-gap={4}
              style={{ rowGap: '1rem' }}
            >
              {rowData.countries.map((country) => {
                const zoneCountry = Countries.getName(country, 'en')
                if (!zoneCountry) {
                  return null
                }
                return (
                  <Chip
                    key={country}
                    size="medium"
                    label={zoneCountry || country}
                    variant="filled"
                    sx={{
                      '& .MuiChip-label': {
                        minWidth: '4rem',
                        fontSize: '1.6rem',
                      },
                    }}
                  />
                )
              })}
            </Stack>
          )
        },
      },
      {
        title: messages[intl.generic('zone-allocations')],
        field: 'allocation',
        width: '10%',
        customFilterAndSearch: (term, rowData) => {
          return rowData?.userData?.role
            ?.toLowerCase()
            ?.includes(term?.toLowerCase())
        },
      },
      {
        title: messages[intl.generic('zone-usage')],
        field: 'usage',
        width: '10%',
      },
    ]
  }

  const invalidateQuery = () => {
    // TODO after api integration
    queryClient.invalidateQueries(['getCustomer', selectedCustomer])
    queryClient.invalidateQueries(['getCustomerPlan', selectedCustomer])
  }

  const handleDeleteZoneSubscription = () => {
    setIsDeleteZone(false)
    invalidateQuery()
    setDeleteData(undefined)
  }

  const handleAddOrEditZoneSubscription = () => {
    setIsAddOrEditZone(false)
    setEditableData(undefined)
    invalidateQuery()
  }

  const handleCloseAddOrEditZone = () => {
    setIsAddOrEditZone(false)
    setEditableData(undefined)
  }

  return (
    <Sidescroll conditionalySidescroll maxWidth={'819px'}>
      <MuiThemeProvider theme={globalDataTableTheme}>
        <DeleteZone
          zones={zones}
          selectedCustomer={selectedCustomer}
          deleteData={deleteData}
          setOpen={setIsDeleteZone}
          onSubmit={handleDeleteZoneSubscription}
          open={isDeleteZone}
          title={messages[intl.customerZones('zone-delete-title')]}
          content={messages[intl.customerZones('zone-delete-content')]}
        />
        <AddOrEditZone
          customerZones={zones}
          data={editableData}
          setOpen={handleCloseAddOrEditZone}
          onSubmit={handleAddOrEditZoneSubscription}
          open={isAddOrEditZone}
          title={`${editableData ? 'Edit' : 'Add'} Zone`}
        />
        <MaterialTable
          options={{
            pageSize: 50,
            addRowPosition: 'first',
            pageSizeOptions: [50, 100],
          }}
          components={{
            Pagination: (props) => (
              <TablePagination {...props} style={{ paddingRight: '2rem' }} />
            ),
          }}
          localization={localization(messages)}
          elevation={0}
          title={
            showTitle
              ? messages[intl.settings('all-customer-zone-subscriptions')]
              : ''
          }
          columns={columns(messages)}
          icons={icons}
          data={zones}
          actions={[
            {
              icon: icons.Add,
              tooltip: messages[intl.customerZones('zone-add-tooltip')],
              isFreeAction: true,
              onClick: () => {
                setIsAddOrEditZone(true)
                setEditableData(undefined)
              },
            },
            {
              icon: icons.Edit,
              tooltip: messages[intl.customerZones('zone-edit-tooltip')],
              onClick: (event, rowData) => {
                setIsAddOrEditZone(true)
                setEditableData(rowData)
              },
            },
            {
              icon: icons.Delete,
              tooltip: messages[intl.customerZones('zone-delete-tooltip')],
              onClick: (event, rowData) => {
                setDeleteData(rowData)
                setIsDeleteZone(true)
              },
            },
          ]}
        />
      </MuiThemeProvider>
    </Sidescroll>
  )
}

export default GlobalDataTable
